<script setup lang="ts">
import type { Query } from '@directus/types'
import Autoplay from 'embla-carousel-autoplay'

definePageMeta({
  title: 'Electro-Mechanical Assemblies',
  order: 1,
  description: 'Save time by consolidating cables and harnesses into higher level electro-mechanical assemblies.',
})

type ServiceGallery = Pick<Gallery, 'images'> & {
  images: Pick<GalleryFile, 'directus_files_id'> & {
    directus_files_id: Pick<DirectusFile, 'id' | 'title' | 'description'>
  }[]
}

const query: Query = {
  fields: [
    'images.directus_files_id.id',
    'images.directus_files_id.title',
    'images.directus_files_id.description',
  ],
}

const { data: gallery } = await useFetch(
  '/api/items/galleries/electromech-assemblies',
  {
    key: 'page:electromech-assemblies:gallery',
    query,
    lazy: true,
    transform: (gallery: ServiceGallery) => gallery.images.map(image => image.directus_files_id),
    getCachedData: (key, nuxtApp) => nuxtApp.payload.data[key],
  },
)
</script>

<template>
  <section>
    <BlockContent>
      <h1 class="subpage-title">
        Electro-Mechanical Assemblies for Full Integration
      </h1>
      <h2 class="subpage-subtitle mt-2">
        We'll meet you half-way
      </h2>
      <ul class="my-18 flex flex-wrap gap-y-8 f-text-16-20 px-card f-gap-x-44-64">
        <div class="space-y-8">
          <li class="flex gap-4 items-center">
            <div class="i-carbon-intent-request-scale-in inline-block scale-120 bg-gradient-icsbluecyan" />Consolidate cables and harnesses into higher level assemblies
          </li>
          <li class="flex gap-4 items-center">
            <div class="i-carbon-forecast-lightning inline-block scale-120 bg-gradient-icsbluecyan" />Ready for revisions and rapid iterations
          </li>
          <li class="flex gap-4 items-center">
            <div class="i-carbon-hourglass inline-block scale-120 bg-gradient-icsbluecyan" />Shorten time to market
          </li>
        </div>
        <li class="space-y-4">
          <div class="flex gap-4 items-center">
            <div class="i-carbon-intent-request-scale-out inline-block scale-120 bg-gradient-icsbluecyan" />Diverse capabilities, including:
          </div>
          <ul class="text-icssilver-800 leading-7 pl-10 list-square list-inside f-text-14-17 dark:text-icssilver-300 marker:text-icsgray-500">
            <li>
              Subsystem assemblies
              <ul class="pl-5 list-square list-inside">
                <li>Board-level interconnects chassis wiring</li>
                <li>PCB wiring</li>
              </ul>
            </li>
            <li>Power supply assemblies</li>
            <li>Box builds: full system assemblies</li>
            <li>Panel assemblies</li>
            <li>Din rail assemblies</li>
            <li>Transformer assemblies</li>
            <li>Switch & sensor assemblies (i.e. foot switch)</li>
            <!-- eslint-disable-next-line -->
            <li><NuxtLink to="/capabilities" class="link">More</NuxtLink></li>
          </ul>
        </li>
      </ul>
      <nav class="mb-10 flex flex-wrap gap-6">
        <AButton
          to="/contact/rfq"
          label="Get a Quote"
          size="xlarge"
          color="cyan"
        />
        <AButton
          label="Cable Assemblies & Harnesses"
          to="/services/cable-assemblies"
          size="xlarge"
          color="blue"
          icon="i-carbon-arrow-right "
          icon-side="right"
          material="empty"
          class="max-sm:w-full"
          body-class="justify-start"
          label-class="max-sm:whitespace-normal max-sm:text-left"
        />
      </nav>
    </BlockContent>
    <div class="py-10">
      <BlockContent>
        <h4 class="text-xl heading-strong drop-shadow dark:mb-4">
          Built by ICS:
        </h4>
      </BlockContent>
      <ACarousel
        :plugins="[Autoplay({ delay: 8000 })]"
        nav
        :clip="false"
        use-slide-css
        class="items-center"
      >
        <div
          v-for="item in gallery"
          :key="item.id"
          class="shrink-0 grow-0 basis-full max-w-full min-w-0 f-px-24-48 2xl:basis-1/3 4xl:basis-1/4 md:basis-1/2"
        >
          <NuxtImg
            :src="item.id"
            :alt="item.description || item.title || 'Custom ICS electro-mechanical assembly.'"
            class="rounded-xs object-contain size-full dark:opacity-90"
          />
        </div>
      </ACarousel>
    </div>
  </section>
</template>
